/**
 * Host Module Federation runtime plugin: a remote whose `remoteEntry.js` fails
 * to load degrades to a stub container instead of rejecting share init (which
 * `version-first` runs against every remote before `src/bootstrap.tsx` boots).
 *
 * - `loadEntryError`, not `errorLoadRemote`: share init awaits each remote's
 *   `init()` without ever emitting `errorLoadRemote`.
 * - Covers script-load failures only; not a throwing container `init()`, nor a
 *   static value import from the remote in the boot graph (that still throws).
 * - MF caches the stub for the page lifetime; never call `getRemoteEntry` here
 *   (that retries). Must stay the only `loadEntryError` listener, as AsyncHook
 *   returns the last listener's value.
 * - The error must not look chunk-shaped, or `tryHandleChunkError` reloads.
 *
 * Bundled into the host runtime ahead of share init, so: type imports only.
 * See docs/Module-Federation.md, "Ship precondition for a new remote".
 */
import type { ModuleFederationRuntimePlugin } from "@module-federation/runtime";
import type { RemoteEntryExports } from "@module-federation/runtime/types";

export const REMOTE_ENTRY_FAILURE_PLUGIN_NAME = "webapp-remote-entry-failure";

export class RemoteEntryUnavailableError extends Error {
  constructor(remoteName: string, entryUrl: string) {
    super(
      `Module Federation remote "${remoteName}" is unavailable: its remoteEntry.js (${entryUrl}) failed to load.`
    );
    this.name = "RemoteEntryUnavailableError";
  }
}

const createUnavailableContainer = (
  error: RemoteEntryUnavailableError
): RemoteEntryExports => ({
  init: () => undefined,
  get: (): never => {
    throw error;
  },
});

const remoteEntryFailureRuntimePlugin = (): ModuleFederationRuntimePlugin => ({
  name: REMOTE_ENTRY_FAILURE_PLUGIN_NAME,
  // Mirrors runtime-core's nested hook type; an inferred return type fails it.
  async loadEntryError({
    remoteInfo,
  }): Promise<Promise<RemoteEntryExports | undefined> | undefined> {
    const error = new RemoteEntryUnavailableError(
      remoteInfo.name,
      remoteInfo.entry
    );
    console.warn(
      `[mf] ${error.message} Degrading to the bridge's error boundary.`
    );
    return createUnavailableContainer(error);
  },
});

export default remoteEntryFailureRuntimePlugin;
